import React, { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";

/**
 * LanguagePicker — drop-in dropdown that drives the hidden Google Translate widget.
 *
 * How it works:
 *  - index.html loads translate_a/element.js which injects a hidden <select.goog-te-combo>.
 *  - Picking a language sets a `googtrans` cookie (Google's contract) AND programmatically
 *    fires a `change` event on that hidden select. Both paths exist because the cookie alone
 *    only takes effect on the next reload, whereas the change event swaps copy in-place.
 *  - The chosen code is persisted to localStorage so a returning visitor stays in their language.
 */

// Curated language list — kept compact; Google supports 100+ but a long menu kills UX.
const LANGUAGES = [
  { code: "en",     label: "English",    flag: "🇺🇸" },
  { code: "es",     label: "Español",    flag: "🇪🇸" },
  { code: "fr",     label: "Français",   flag: "🇫🇷" },
  { code: "de",     label: "Deutsch",    flag: "🇩🇪" },
  { code: "it",     label: "Italiano",   flag: "🇮🇹" },
  { code: "pt",     label: "Português",  flag: "🇵🇹" },
  { code: "nl",     label: "Nederlands", flag: "🇳🇱" },
  { code: "sv",     label: "Svenska",    flag: "🇸🇪" },
  { code: "pl",     label: "Polski",     flag: "🇵🇱" },
  { code: "ru",     label: "Русский",    flag: "🇷🇺" },
  { code: "uk",     label: "Українська", flag: "🇺🇦" },
  { code: "tr",     label: "Türkçe",     flag: "🇹🇷" },
  { code: "ar",     label: "العربية",    flag: "🇸🇦" },
  { code: "he",     label: "עברית",      flag: "🇮🇱" },
  { code: "hi",     label: "हिन्दी",     flag: "🇮🇳" },
  { code: "ja",     label: "日本語",      flag: "🇯🇵" },
  { code: "ko",     label: "한국어",      flag: "🇰🇷" },
  { code: "zh-CN",  label: "简体中文",     flag: "🇨🇳" },
  { code: "zh-TW",  label: "繁體中文",     flag: "🇹🇼" },
];

const STORAGE_KEY = "aip_lang";

function setGoogTransCookie(target) {
  // Format Google expects: /<source>/<target> ; without a leading dot the cookie sticks
  // to the exact host, which is what we want on a custom domain.
  const value = `/en/${target}`;
  document.cookie = `googtrans=${value}; path=/`;
  // Also set on the bare domain for sub-domain safety (e.g. www.aipresidential.com → aipresidential.com)
  try {
    const host = window.location.hostname.replace(/^www\./, "");
    document.cookie = `googtrans=${value}; path=/; domain=.${host}`;
  } catch (_) { /* ignore */ }
}

function triggerGoogleTranslate(code) {
  const select = document.querySelector("select.goog-te-combo");
  if (!select) return false;
  select.value = code;
  select.dispatchEvent(new Event("change"));
  return true;
}

export default function LanguagePicker({ variant = "ghost" }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);

  // Restore previous choice + apply when the widget finishes loading
  useEffect(() => {
    let saved = "en";
    try { saved = localStorage.getItem(STORAGE_KEY) || "en"; } catch (_) { /* ignore */ }
    setCurrent(saved);
    if (saved === "en") return;

    setGoogTransCookie(saved);
    const apply = () => triggerGoogleTranslate(saved);
    if (!apply()) {
      // Widget hasn't injected the <select> yet — wait for our gt-ready event
      const onReady = () => {
        // Tiny delay so Google can finish wiring the select before we touch it
        setTimeout(apply, 50);
        window.removeEventListener("gt-ready", onReady);
      };
      window.addEventListener("gt-ready", onReady);
      return () => window.removeEventListener("gt-ready", onReady);
    }
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (code) => {
    setCurrent(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* ignore */ }
    setGoogTransCookie(code);
    const ok = triggerGoogleTranslate(code);
    if (!ok) {
      // Widget not ready yet — force a reload so the cookie takes effect server-side
      window.location.reload();
      return;
    }
    setOpen(false);
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  const btnBase = "inline-flex items-center gap-2 rounded-full text-sm font-medium transition-colors";
  const btnVariant =
    variant === "solid"
      ? "px-3 py-1.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white"
      : "px-3 py-1.5 border border-white/10 hover:border-cyan-400/40 text-gray-300 hover:text-white";

  return (
    <div ref={ref} className="relative" data-testid="language-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${btnBase} ${btnVariant}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-picker-button"
        translate="no"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline" translate="no">{active.flag} {active.label}</span>
        <span className="sm:hidden" translate="no">{active.flag}</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-56 max-h-80 overflow-y-auto rounded-2xl bg-black/95 backdrop-blur border border-white/10 shadow-2xl z-[200] py-2"
          data-testid="language-picker-menu"
          translate="no"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={l.code === current}
              onClick={() => choose(l.code)}
              className={`w-full px-4 py-2 flex items-center justify-between text-sm transition-colors ${
                l.code === current ? "text-cyan-300 bg-cyan-400/10" : "text-gray-300 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`language-option-${l.code}`}
              translate="no"
            >
              <span className="flex items-center gap-3"><span className="text-base">{l.flag}</span>{l.label}</span>
              {l.code === current && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
